import { inject, Injectable } from '@angular/core'

import { MessagesService } from '@shared/services'
import { Badge } from '../../models'
import { SalaDeTrofeosApi } from '../sala-de-trofeos-api/sala-de-trofeos-api'

/**
 * SalaDeTrofeosService - Business logic and orchestration for badge (insignia) operations
 *
 * Responsibility: Public API for badge operations, orchestrates API layer with business logic.
 *
 * Rules:
 * - Combines SalaDeTrofeosApi (goals + seen entries)
 * - Filters out badges the participant hasn't achieved or has already been shown
 * - Returns Promises for all operations
 * - This is the ONLY service exported in public API (features/sala-de-trofeos/index.ts)
 */
@Injectable({
  providedIn: 'root'
})
export class SalaDeTrofeosService {
  #salaDeTrofeosApi = inject(SalaDeTrofeosApi)
  #messagesService = inject(MessagesService)

  /**
   * Get every badge (achieved or in progress), for the sala de trofeos listing page.
   * @returns Promise<Badge[]>
   */
  async getBadges(): Promise<Badge[]> {
    try {
      return await this.#salaDeTrofeosApi.getBadgeGoals()
    } catch {
      this.#messagesService.error('No se pudieron cargar las insignias')
      return []
    }
  }

  /**
   * Get achieved badges that haven't been shown to the participant yet.
   * @returns Promise<Badge[]>
   */
  async getUnseenBadges(): Promise<Badge[]> {
    try {
      const [badges, seenEntries] = await Promise.all([this.#salaDeTrofeosApi.getBadgeGoals(), this.#salaDeTrofeosApi.getSeenBadgeEntries()])

      const seenCategories = seenEntries.map(entry => entry.category)

      return badges.filter(badge => badge.achieved && !seenCategories.includes(badge.category))
    } catch {
      this.#messagesService.error('No se pudieron cargar las insignias')
      return []
    }
  }

  /**
   * Mark a badge's category as seen, so it stops showing up in getUnseenBadges().
   * @param category - Badge category (Badge.category)
   * @returns Promise<boolean> - true when the external entry was registered
   */
  async markBadgeAsSeen(category: string): Promise<boolean> {
    try {
      await this.#salaDeTrofeosApi.markBadgeAsSeen(category)
      return true
    } catch {
      this.#messagesService.error('No se pudo registrar la insignia')
      return false
    }
  }
}
